import { css, type SerializedStyles } from '@emotion/react';

import { COLORS2, LAYOUT2 } from '@/Client/Styles';
import { styleguideFontFamily as FONT } from '@kaiizen/kai-shiki/core';

import { pxToRem } from '@/Client/Styles/pxToRem';
import { DARK_BG } from '../scanVideoMediaStyles';
import {
  DEFAULT_VIDEO_PLAYBACK_PANEL_STYLES,
  type VideoPlaybackPanelStyles,
} from './playback/videoPlaybackPanelStyles';

/**
 * Video toolbox presentation bundled into one object — same pattern as
 * `FocusedStripStyles` / `DEFAULT_FOCUSED_STRIP_STYLES`.
 */
export type VideoToolboxPanelStyles = {
  panelBody: SerializedStyles;
  stickyVideo: SerializedStyles;
  playbackPanel: VideoPlaybackPanelStyles;
  panelActionFooter: SerializedStyles;
  footHint: SerializedStyles;
  footHintError: SerializedStyles;
};

/** FlyOut content wrapper overrides — flush video to header; keep default light drawer chrome. */
export const VIDEO_TOOLBOX_DRAWER_STYLE = css`
  label: VIDEO_TOOLBOX_DRAWER;

  & > div:nth-of-type(2) {
    padding-top: 0;
    gap: 0;
    flex: 1;
    min-height: 0;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }

  footer {
    padding: 0;
  }
`;

export const DEFAULT_VIDEO_TOOLBOX_PANEL_STYLES: VideoToolboxPanelStyles = {
  panelBody: css`
    label: VIDEO_TOOLBOX_PANEL_BODY;
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    gap: 0;
  `,
  stickyVideo: css`
    label: VIDEO_TOOLBOX_STICKY_VIDEO;
    position: sticky;
    top: 0;
    z-index: 9;
    background: ${DARK_BG};
    flex-shrink: 0;
  `,
  playbackPanel: {
    ...DEFAULT_VIDEO_PLAYBACK_PANEL_STYLES,
    frame: css`
      label: VIDEO_TOOLBOX_PLAYBACK_FRAME;
      position: relative;
      background: ${DARK_BG};
      border: 0;
      margin-top: 0;
    `,
    video: css`
      label: VIDEO_TOOLBOX_PLAYBACK_VIDEO;
      display: block;
      width: 100%;
      max-height: min(42vh, 380px);
      aspect-ratio: 16 / 9;
      object-fit: contain;
      cursor: pointer;
    `,
    overlayBar: css`
      label: VIDEO_TOOLBOX_PLAYBACK_OVERLAY_BAR;
      position: absolute;
      left: 0;
      right: 0;
      bottom: 0;
      padding: 22px 14px 10px;
      background: linear-gradient(to top, rgba(0, 0, 0, 0.72), rgba(0, 0, 0, 0));
      z-index: 3;
      pointer-events: none;

      & > * {
        pointer-events: auto;
      }
    `,
    timeline: css`
      label: VIDEO_TOOLBOX_PLAYBACK_TIMELINE;
      position: relative;
      user-select: none;
    `,
    controls: css`
      label: VIDEO_TOOLBOX_PLAYBACK_CONTROLS;
      display: flex;
      align-items: center;
      gap: 12px;
      margin-top: 8px;
      padding: 0;
    `,
  },
  panelActionFooter: css`
    label: VIDEO_TOOLBOX_PANEL_ACTION_FOOTER;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${LAYOUT2.spacing1}px;
    width: 100%;
  `,
  footHint: css`
    label: VIDEO_TOOLBOX_FOOT_HINT;
    flex: 1;
    min-width: 0;
    font-family: ${FONT.mono};
    font-size: ${pxToRem(10)};
    font-weight: 700;
    letter-spacing: 0.05em;
    color: var(--text2);
    text-transform: uppercase;
  `,
  footHintError: css`
    color: ${COLORS2.alwaysDark.red};
  `,
};
